<template>
  <Panel
    lineColor="var(--primary-color)"
    class="rfm-panel"
    id="card-simulador-regras-rfm"
  >
    <div class="section-header">
      <div>
        <h3 class="section-title">Simulador de alocação e score RFM</h3>
        <p class="section-subtitle">
          Informe as métricas de um cliente para ver as notas de 1 a 5, o score
          composto e o grupo em que ele é alocado. A simulação considera as
          faixas em edição nesta tela, inclusive as ainda não salvas.
        </p>
      </div>
      <FormButton
        outlined
        type="primario"
        color="primario"
        id="btn-toggle-guia-segmentos"
        :text="
          guiaAberto ? 'Ocultar guia de grupos' : 'Como funcionam os grupos?'
        "
        @click="guiaAberto = !guiaAberto"
      />
    </div>

    <div v-if="guiaAberto" class="rfm-guia">
      <p class="nota-informativa">
        <v-icon size="14" color="primario">fas fa-info-circle</v-icon>
        O motor gera três notas de <strong>1 a 5</strong> (R de Recência, F de
        Frequência e M de Valor Monetário), formando um código de três dígitos
        como <strong>555</strong>, <strong>543</strong> ou <strong>221</strong>.
        A combinação define o grupo do cliente.
      </p>
      <v-row>
        <v-col
          v-for="segmento in segmentos"
          :key="`guia-${segmento.nome}`"
          cols="12"
          sm="6"
          lg="4"
        >
          <div class="rfm-card">
            <Chip color="primario" :text="segmento.nome" small />
            <p class="rfm-card-texto">{{ segmento.descricao }}</p>
          </div>
        </v-col>
      </v-row>
    </div>

    <v-row>
      <v-col cols="12" md="4">
        <div class="rfm-card">
          <div class="rfm-card-header">
            <label class="field-label">Recência (R)</label>
            <Chip
              v-if="resultado"
              color="primario"
              :text="`Nota ${resultado.r}`"
              small
            />
          </div>
          <FormField
            id="simulador-input-dias"
            typeField="number"
            step="1"
            minField="0"
            :value="insumos.diasDesdeUltimoPedido"
            @input="valor => alterarInsumo('diasDesdeUltimoPedido', valor)"
          />
          <small class="field-hint">Dias desde a última compra.</small>
        </div>
      </v-col>

      <v-col cols="12" md="4">
        <div class="rfm-card">
          <div class="rfm-card-header">
            <label class="field-label">Frequência (F)</label>
            <Chip
              v-if="resultado"
              color="primario"
              :text="`Nota ${resultado.f}`"
              small
            />
          </div>
          <FormField
            id="simulador-input-pedidos"
            typeField="number"
            step="1"
            minField="0"
            :value="insumos.qtdPedidosJanela"
            @input="valor => alterarInsumo('qtdPedidosJanela', valor)"
          />
          <small class="field-hint">Pedidos no período de análise.</small>
        </div>
      </v-col>

      <v-col cols="12" md="4">
        <div class="rfm-card">
          <div class="rfm-card-header">
            <label class="field-label">Valor Monetário (M)</label>
            <Chip
              v-if="resultado"
              color="primario"
              :text="`Nota ${resultado.m}`"
              small
            />
          </div>
          <FormField
            id="simulador-input-valor"
            typeField="number"
            step="0.01"
            minField="0"
            :value="insumos.somaTotalliquidoJanela"
            @input="valor => alterarInsumo('somaTotalliquidoJanela', valor)"
          />
          <small class="field-hint">Total faturado no período, em reais.</small>
        </div>
      </v-col>
    </v-row>

    <p v-if="bloqueado" class="nota-informativa nota-alerta">
      <v-icon size="14" color="warning">fas fa-exclamation-circle</v-icon>
      Corrija as inconsistências das faixas para simular a alocação.
    </p>

    <p v-else-if="carregando" class="nota-informativa">
      <v-progress-circular indeterminate size="14" width="2" color="primario" />
      Simulando com as faixas atuais...
    </p>

    <p v-else-if="erro" class="nota-informativa nota-erro" id="simulador-erro">
      <v-icon size="14" color="error">fas fa-exclamation-circle</v-icon>
      {{ erro }}
    </p>

    <div
      v-else-if="resultado"
      class="rfm-card rfm-resultado"
      id="card-resultado-alocacao-cliente"
    >
      <div>
        <div class="rfm-card-header">
          <span class="field-label">Grupo de alocação do cliente:</span>
          <Chip color="primario" :text="resultado.segmentoRfm" small />
        </div>
        <p class="rfm-card-texto">{{ copySegmentoAtual.descricao }}</p>
        <p v-if="copySegmentoAtual.acao" class="rfm-card-texto">
          <strong>Ação recomendada:</strong> {{ copySegmentoAtual.acao }}
        </p>
      </div>
      <div class="rfm-score">
        <div class="rfm-score-label">Score composto</div>
        <div class="rfm-score-valor">{{ resultado.scoreRfm }}</div>
        <div class="rfm-score-detalhe">
          R {{ resultado.r }} · F {{ resultado.f }} · M {{ resultado.m }}
        </div>
      </div>
    </div>
  </Panel>
</template>

<script>
import Panel from "@/components/Panel";
import FormButton from "@/components/form/FormButton";
import FormField from "@/components/form/FormField";
import Chip from "@/components/Chip";
import { copySegmento } from "@/utils/rfm-segmentos";

export default {
  name: "MatrizRfmSimulador",
  components: { Panel, FormButton, FormField, Chip },
  props: {
    insumos: { type: Object, required: true },
    /** Resultado do POST /simular. Nunca é calculado no frontend. */
    resultado: { type: Object, default: null },
    carregando: { type: Boolean, default: false },
    erro: { type: String, default: "" },
    bloqueado: { type: Boolean, default: false },
    segmentos: { type: Array, default: () => [] }
  },
  data: () => ({
    guiaAberto: false
  }),
  computed: {
    copySegmentoAtual() {
      return copySegmento(this.resultado ? this.resultado.segmentoRfm : "");
    }
  },
  methods: {
    alterarInsumo(campo, valor) {
      this.$emit("alterar", { campo, valor });
    }
  }
};
</script>

<style scoped>
.rfm-panel {
  margin-bottom: 20px;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.section-title,
.section-subtitle,
.nota-informativa,
.field-label,
.field-hint,
.rfm-card-texto {
  text-align: left;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 4px;
}

.section-subtitle {
  color: #6a6c6f;
  font-size: 16px;
  margin-bottom: 16px;
}

.nota-informativa {
  background: #f5f7fa;
  border-left: 3px solid var(--primary-color);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 16px;
  color: #6a6c6f;
}

.nota-informativa .v-icon,
.nota-informativa .v-progress-circular {
  margin-right: 8px;
}

.nota-alerta {
  border-left-color: var(--alert-color);
}

.nota-erro {
  border-left-color: var(--danger-color);
}

.rfm-card {
  height: 100%;
  border: 1px solid #e4e5e7;
  border-radius: 8px;
  padding: 16px;
}

.rfm-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.rfm-card-texto {
  color: #6a6c6f;
  margin: 8px 0 0;
}

.field-label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
}

.field-hint {
  display: block;
  color: #6a6c6f;
}

.rfm-guia {
  margin-bottom: 16px;
}

.rfm-resultado {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.rfm-score {
  min-width: 150px;
  text-align: center;
}

.rfm-score-label,
.rfm-score-detalhe {
  color: #6a6c6f;
  font-size: 12px;
}

.rfm-score-valor {
  font-size: 32px;
  font-weight: 700;
  letter-spacing: 3px;
  color: var(--primary-color);
}
</style>
